/**
 * Normalizes a warehouse or Census county label for comparison.
 * @param name - Raw county name.
 * @returns Lowercase label without a trailing "County".
 */
export const normalizeCountyLabel = (name: string): string =>
  name
    .replace(/\s*County$/i, '')
    .replace(/\./g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const COUNTY_ALIASES: Record<string, string> = {
  'saint clair': 'st clair',
  'st clair': 'st clair',
  'saint joseph': 'st joseph',
  'st joseph': 'st joseph',
  'saint louis': 'st louis',
  'st louis': 'st louis',
};

/**
 * Applies known county name aliases after {@link normalizeCountyLabel}.
 * @param normalized - Normalized county label.
 * @returns Canonical normalized key for matching.
 */
export const canonicalCountyKey = (normalized: string): string =>
  COUNTY_ALIASES[normalized] ?? normalized;

/**
 * Finds the Census/topo county name that matches a warehouse `google_county` value.
 * @param googleCounty - County label from BigQuery.
 * @param topoCountyNames - County `properties.name` values for the state.
 * @returns Matching topo name or null.
 */
export const matchGoogleCountyToTopoName = (
  googleCounty: string,
  topoCountyNames: string[],
): string | null => {
  const key = canonicalCountyKey(normalizeCountyLabel(googleCounty));
  if (!key) {
    return null;
  }
  for (const topoName of topoCountyNames) {
    if (canonicalCountyKey(normalizeCountyLabel(topoName)) === key) {
      return topoName;
    }
  }
  return null;
};
